import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  MenuListProps,
  MultiValueRemoveProps,
  InputActionMeta,
} from "react-select";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";
import { Option } from "../../interface/Option";

interface SpecialtyOption extends Option {
  __inactive?: boolean;
  is_active?: boolean | number | string | null;
  status?: string;
  Status?: string;
  is_deleted?: boolean;
}

interface SpecialtyProviderSelectorProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  value?: Option[];
}

const inactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  if (item.__inactive === true || item.is_deleted === true) return true;
  const state = item.is_active ?? item.Is_active ?? item.isActive ?? item.active ?? item.status ?? item.Status;
  return state === false || state === 0 || state === "0" ||
    ["inactive", "disabled", "false"].includes(String(state ?? "").trim().toLowerCase());
};

const specialtyKey = (item: SpecialtyOption) => String(item.value ?? item.label ?? "").trim().toLowerCase();

const extract = (response: any): any[] => {
  const root = response?.data;
  const candidates = [
    root?.result?.data, root?.result?.rows, root?.result?.result,
    root?.result, root?.data?.data, root?.data?.rows, root?.data,
    root?.rows, root?.specialties, root?.specialities, root?.specialtyProviders,
    root?.specialty_providers, root?.getSepcialtyProvider, root,
  ];
  return candidates.find(Array.isArray) ?? [];
};

const format = (items: any[]): SpecialtyOption[] => items.flatMap((item: any) => {
  const id = item?.id ?? item?.speciality_id ?? item?.specialty_id;
  const name = String(
    item?.name ?? item?.speciality_name ?? item?.specialty_name ??
    item?.Speciality_name ?? item?.Specialty_name ?? item?.speciality ?? item?.specialty ?? ""
  ).trim();
  if (id == null || !name) return [];
  return [{
    value: String(id), label: name,
    is_active: item?.is_active ?? item?.Is_active ?? item?.isActive ?? item?.active,
    status: item?.status ?? item?.Status,
    is_deleted: item?.is_deleted,
    __inactive: inactive(item),
  }];
});

const fetchSpecialties = async (): Promise<SpecialtyOption[]> => {
  // Provider specialties are a separate catalogue from Doctor specialties.
  // Never substitute getSpecialty here.
  const all = new Map<string, SpecialtyOption>();
  const loadPages = async (active?: string): Promise<void> => {
    for (let page = 1; page <= 30; page++) {
      const response = await providerAxiosInstance.get("getSepcialtyProvider", {
        params: {
          page,
          limit: 100,
          is_deleted: "false",
          ...(active === undefined ? {} : { active }),
        },
      });
      const rows = extract(response);
      for (const option of format(rows)) {
        const key = specialtyKey(option);
        const previous = all.get(key);
        // An endpoint may ignore `active`; do not overwrite known status with
        // a duplicate item whose response lacks status metadata.
        if (!previous || (
          previous.is_active == null && previous.status == null &&
          (option.is_active != null || option.status != null)
        )) {
          all.set(key, option);
        }
      }
      const pagination = response?.data?.pagination ?? response?.data?.result?.pagination;
      const totalPages = Number(pagination?.totalPages ?? pagination?.total_pages);
      if (rows.length === 0 || (Number.isFinite(totalPages) && totalPages > 0
        ? page >= totalPages : rows.length < 100)) break;
    }
  };

  // First use the original Provider endpoint without an active filter.
  // Some backends do not support the active query parameter on this route.
  await loadPages();
  if (all.size === 0) {
    await loadPages("true");
  }
  return Array.from(all.values());
};

const SpecialtyProviderSelector = ({ onChange, clear, defaultValue, value }: SpecialtyProviderSelectorProps) => {
  const [allOptions, setAllOptions] = useState<SpecialtyOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<SpecialtyOption[]>(
    (value ?? defaultValue ?? []) as SpecialtyOption[]
  );
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      setAllOptions(await fetchSpecialties());
    } catch (error) {
      console.error("Error loading specialties:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadOptions(); }, [loadOptions]);
  useEffect(() => {
    const incoming = value ?? defaultValue;
    if (Array.isArray(incoming)) {
      setSelectedOptions(incoming as SpecialtyOption[]);
    }
  }, [value, defaultValue]);
  useEffect(() => { if (clear) { setSelectedOptions([]); setInputValue(""); } }, [clear]);

  // Matching on the SQL ID first avoids treating two specialties with similar names as one.
  // Retain the parent-provided label and value to preserve the existing update payload.
  const enriched = selectedOptions.map((option) => {
    const found = allOptions.find((candidate) => specialtyKey(candidate) === specialtyKey(option)) ??
      allOptions.find((candidate) => candidate.label.toLowerCase() === String(option.label).toLowerCase());
    const isOff = found ? inactive(found) : inactive(option);
    return { ...option, __inactive: isOff };
  });

  const handleChange = (selected: MultiValue<SpecialtyOption>) => {
    const requested = [...selected];
    const locked = enriched.filter(inactive);
    const next = [...requested];
    for (const option of locked) {
      if (!next.some((candidate) => specialtyKey(candidate) === specialtyKey(option))) next.push(option);
    }
    const safe = next.filter((option, index) => next.findIndex((x) => specialtyKey(x) === specialtyKey(option)) === index);
    setSelectedOptions(safe);
    onChange(safe);
  };

  const MenuList = (props: MenuListProps<SpecialtyOption, true>) => (
    <components.MenuList {...props}>
      <div onWheelCapture={(e) => e.stopPropagation()} onTouchMoveCapture={(e) => e.stopPropagation()}>
        {props.children}
      </div>
    </components.MenuList>
  );
  const MultiValueRemove = (props: MultiValueRemoveProps<SpecialtyOption, true>) =>
    inactive(props.data) ? null : <components.MultiValueRemove {...props} />;

  const styles: StylesConfig<SpecialtyOption, true> = {
    control: (base) => ({ ...base, minHeight: "40px", fontSize: "14px", borderRadius: "8px", cursor: "pointer" }),
    valueContainer: (base) => ({ ...base, fontSize: "14px" }),
    input: (base) => ({ ...base, fontSize: "14px" }),
    multiValue: (base, props) => ({ ...base, backgroundColor: inactive(props.data) ? "#d1d5db" : "#007BFF", borderRadius: "14px", padding: "2px 4px" }),
    multiValueLabel: (base, props) => ({ ...base, color: inactive(props.data) ? "#334155" : "#FFFFFF", fontSize: "12px", padding: "0 4px" }),
    multiValueRemove: (base, props) => ({ ...base, display: inactive(props.data) ? "none" : "flex", color: "#FFFFFF", padding: "2px", cursor: "pointer", ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" } }),
    option: (base, { isSelected, isFocused }) => ({ ...base, backgroundColor: isSelected ? "#007BFF" : isFocused ? "#E6F0FA" : "#FFFFFF", color: isSelected ? "#FFFFFF" : "#000000", fontSize: "14px", padding: "8px 10px", cursor: "pointer" }),
    placeholder: (base) => ({ ...base, fontSize: "14px", color: "#6B7280" }),
    menu: (base) => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
    menuPortal: (base) => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
    menuList: (base) => ({ ...base, maxHeight: "220px", overflowY: "auto", pointerEvents: "auto" }),
  };

  const query = inputValue.trim().toLowerCase();
  const selectable = allOptions.filter((option) => !inactive(option) && (!query || option.label.toLowerCase().includes(query)));
  return (
    <Select
      isMulti
      isClearable={false}
      value={enriched}
      onChange={handleChange}
      options={selectable}
      isLoading={loading}
      onInputChange={(next: string, meta: InputActionMeta) => { if (meta.action === "input-change") setInputValue(next); return next; }}
      placeholder="Select specialties..."
      noOptionsMessage={() => loading ? "Loading..." : "No specialties found"}
      formatOptionLabel={(option, meta) => meta.context === "value" && inactive(option) ? `${option.label} (Inactive)` : option.label}
      styles={styles}
      components={{ MenuList, MultiValueRemove }}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
      menuPlacement="bottom"
      menuShouldScrollIntoView={false}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      captureMenuScroll={false}
      classNamePrefix="specialty-provider-select"
    />
  );
};

export default SpecialtyProviderSelector;
